import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Boxes, Loader2, MapPin, Save, Wrench } from "lucide-react";

import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { assetsService } from "@/services/assets.service";
import { getErrorMessage } from "@/services/api";
import {
    ASSET_CATEGORIES, OPERATIONAL_STATUS_OPTIONS,
} from "@/config/constants";

const schema = z.object({
    category: z.string().min(1, "Escolha uma categoria"),
    customer_id: z.string().min(1, "Cliente é obrigatório"),
    manufacturer: z.string().optional().nullable(),
    model: z.string().optional().nullable(),
    serial_number: z.string().optional().nullable(),
    customer_asset_tag: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    // Dados técnicos
    power: z.string().optional().nullable(),
    voltage: z.string().optional().nullable(),
    current: z.string().optional().nullable(),
    rpm: z.string().optional().nullable(),
    frequency: z.string().optional().nullable(),
    weight: z.string().optional().nullable(),
    manufacturing_year: z
        .coerce.number().int().min(1900).max(2100)
        .optional().nullable().or(z.literal("")),
    // Localização
    unit: z.string().optional().nullable(),
    sector: z.string().optional().nullable(),
    installation_location: z.string().optional().nullable(),
    operational_status: z.string(),
});

function Field({ label, error, children, className = "", required = false }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-xs font-medium">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            {children}
            {error && <div className="text-[11px] text-danger">{error}</div>}
        </div>
    );
}

function SectionHeader({ icon: Icon, title, hint }) {
    return (
        <div className="flex items-center gap-2 border-b pb-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
            </div>
            <div>
                <div className="text-sm font-semibold">{title}</div>
                {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
            </div>
        </div>
    );
}

/**
 * NewEquipmentDialog — creates a new Equipment inline, without leaving the
 * Work Order form. Includes ALL technical fields per PRD Módulo 02 (1b).
 * Photos are intentionally excluded — user can add them later on the
 * equipment details page.
 */
export function NewEquipmentDialog({
    open,
    onOpenChange,
    customerId,
    customerName,
    onCreated,
}) {
    const qc = useQueryClient();

    const {
        register, handleSubmit, reset, watch, setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            category: "Motor Elétrico",
            customer_id: customerId || "",
            manufacturer: "",
            model: "",
            serial_number: "",
            customer_asset_tag: "",
            description: "",
            power: "",
            voltage: "",
            current: "",
            rpm: "",
            frequency: "",
            weight: "",
            manufacturing_year: "",
            unit: "",
            sector: "",
            installation_location: "",
            operational_status: "operante",
        },
    });

    // Sync customer_id when it changes (parent form controls it)
    useEffect(() => {
        setValue("customer_id", customerId || "");
    }, [customerId, setValue]);

    // Reset when dialog is closed
    useEffect(() => {
        if (!open) {
            reset({
                category: "Motor Elétrico",
                customer_id: customerId || "",
                manufacturer: "",
                model: "",
                serial_number: "",
                customer_asset_tag: "",
                description: "",
                power: "",
                voltage: "",
                current: "",
                rpm: "",
                frequency: "",
                weight: "",
                manufacturing_year: "",
                unit: "",
                sector: "",
                installation_location: "",
                operational_status: "operante",
            });
        }
    }, [open, customerId, reset]);

    const create = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            if (payload.manufacturing_year === null || payload.manufacturing_year === "") {
                delete payload.manufacturing_year;
            }
            return assetsService.create(payload);
        },
        onSuccess: (created) => {
            toast.success(`Equipamento ${created?.internal_code || ""} cadastrado com sucesso.`);
            // Invalidate the generic assets list so other pages (list/dashboard) refetch.
            // The parent form is responsible for its customer-scoped cache update
            // via the onCreated callback (to avoid races with pre-selection).
            qc.invalidateQueries({ queryKey: ["assets"] });
            onCreated?.(created);
            onOpenChange(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao cadastrar equipamento.")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl p-0 sm:max-w-3xl"
                data-testid="new-equipment-dialog"
            >
                <DialogHeader className="border-b p-5 pb-3">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Boxes className="h-4 w-4 text-secondary" />
                        Novo equipamento
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Cadastro rápido de equipamento vinculado ao cliente{" "}
                        <span className="font-medium text-foreground">
                            {customerName || "selecionado"}
                        </span>
                        . Fotos podem ser adicionadas depois na tela do equipamento.
                    </DialogDescription>
                </DialogHeader>

                <form
                    onSubmit={handleSubmit(create.mutate)}
                    className="space-y-0"
                    data-testid="new-equipment-form"
                >
                    <ScrollArea className="max-h-[65vh]">
                        <div className="space-y-6 p-5">
                            {/* Informações */}
                            <div className="space-y-3">
                                <SectionHeader icon={Boxes} title="Informações" />
                                <div className="grid gap-3 md:grid-cols-3">
                                    <Field label="Categoria" required error={errors.category?.message}>
                                        <Select
                                            value={watch("category")}
                                            onValueChange={(v) => setValue("category", v)}
                                        >
                                            <SelectTrigger data-testid="new-eq-category">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ASSET_CATEGORIES.map((c) => (
                                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Fabricante">
                                        <Input {...register("manufacturer")} data-testid="new-eq-manufacturer" />
                                    </Field>
                                    <Field label="Modelo">
                                        <Input {...register("model")} data-testid="new-eq-model" />
                                    </Field>
                                    <Field label="Número de série">
                                        <Input {...register("serial_number")} data-testid="new-eq-serial" />
                                    </Field>
                                    <Field label="Patrimônio do cliente">
                                        <Input
                                            {...register("customer_asset_tag")}
                                            placeholder="ex: TAG-45912"
                                            data-testid="new-eq-customer-tag"
                                        />
                                    </Field>
                                    <Field label="Ano de fabricação">
                                        <Input
                                            type="number"
                                            {...register("manufacturing_year")}
                                            placeholder="ex: 2022"
                                            data-testid="new-eq-year"
                                        />
                                    </Field>
                                    <Field label="Situação inicial">
                                        <Select
                                            value={watch("operational_status")}
                                            onValueChange={(v) => setValue("operational_status", v)}
                                        >
                                            <SelectTrigger data-testid="new-eq-op-status">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {OPERATIONAL_STATUS_OPTIONS.map((s) => (
                                                    <SelectItem key={s.value} value={s.value}>
                                                        {s.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Descrição" className="md:col-span-3">
                                        <Textarea
                                            rows={2}
                                            {...register("description")}
                                            placeholder="Descrição adicional (opcional)…"
                                            data-testid="new-eq-description"
                                        />
                                    </Field>
                                </div>
                            </div>

                            {/* Dados Técnicos */}
                            <div className="space-y-3">
                                <SectionHeader
                                    icon={Wrench}
                                    title="Dados Técnicos"
                                    hint="Todos opcionais — preencha o que for aplicável."
                                />
                                <div className="grid gap-3 md:grid-cols-3">
                                    <Field label="Potência">
                                        <Input
                                            {...register("power")}
                                            placeholder="ex: 150 CV / 110 kW"
                                            data-testid="new-eq-power"
                                        />
                                    </Field>
                                    <Field label="Tensão">
                                        <Input
                                            {...register("voltage")}
                                            placeholder="ex: 220/380V"
                                            data-testid="new-eq-voltage"
                                        />
                                    </Field>
                                    <Field label="Corrente">
                                        <Input
                                            {...register("current")}
                                            placeholder="ex: 210 A"
                                            data-testid="new-eq-current"
                                        />
                                    </Field>
                                    <Field label="RPM">
                                        <Input
                                            {...register("rpm")}
                                            placeholder="ex: 1750"
                                            data-testid="new-eq-rpm"
                                        />
                                    </Field>
                                    <Field label="Frequência">
                                        <Input
                                            {...register("frequency")}
                                            placeholder="ex: 60 Hz"
                                            data-testid="new-eq-frequency"
                                        />
                                    </Field>
                                    <Field label="Peso">
                                        <Input
                                            {...register("weight")}
                                            placeholder="ex: 45 kg"
                                            data-testid="new-eq-weight"
                                        />
                                    </Field>
                                </div>
                            </div>

                            {/* Localização */}
                            <div className="space-y-3">
                                <SectionHeader icon={MapPin} title="Localização" />
                                <div className="grid gap-3 md:grid-cols-3">
                                    <Field label="Unidade">
                                        <Input
                                            {...register("unit")}
                                            placeholder="ex: Planta São Paulo"
                                            data-testid="new-eq-unit"
                                        />
                                    </Field>
                                    <Field label="Setor">
                                        <Input
                                            {...register("sector")}
                                            placeholder="ex: Produção A"
                                            data-testid="new-eq-sector"
                                        />
                                    </Field>
                                    <Field label="Local de instalação">
                                        <Input
                                            {...register("installation_location")}
                                            placeholder="ex: Estação 12"
                                            data-testid="new-eq-install-location"
                                        />
                                    </Field>
                                </div>
                            </div>
                        </div>
                    </ScrollArea>

                    <DialogFooter className="gap-2 border-t bg-muted/20 p-4 sm:justify-between">
                        <div className="text-[11px] text-muted-foreground">
                            Os campos técnicos e fotos podem ser complementados depois
                            na tela do equipamento.
                        </div>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="new-eq-cancel"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                className="bg-secondary text-secondary-foreground hover:opacity-90"
                                disabled={create.isPending || !customerId}
                                data-testid="new-eq-save"
                            >
                                {create.isPending
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Save className="mr-2 h-4 w-4" />}
                                Cadastrar equipamento
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default NewEquipmentDialog;
